import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, ViewChild, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MyTasksetService } from '@sbd-doug-frontend/app/modules/my-tasks/my-taskset.service';
import { Account, AccountUserListItem } from '@sbd-doug-frontend/shared/lib/types/account.types';
import {
  TaskCreateFormComponent,
  TaskCreateFormDraft,
} from '@sbd-doug-frontend/shared/lib/shared-components/task-viewer/content/right-content/stage-column/forms/task-create-form/task-create-form.component';
import { CIPXDrawerContentInterface } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-content.interface';
import { DRAWER_DATA, DRAWER_REF, DrawerRef } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-manager.service';
import { CommonDrawerFooterComponent } from '@sbd-doug-frontend/shared/lib/shared-components/common-drawer-footer/common-drawer-footer.component';

export interface AccountManagerActivityFollowUpTaskDrawerData {
  accountId?: number | null;
  accountLabel?: string | null;
  accountUsers?: AccountUserListItem[];
  taskDraft?: TaskCreateFormDraft | null;
  title?: string;
}

@Component({
  selector: 'sbd-doug-frontend-account-manager-activity-follow-up-task-drawer-wrapper',
  standalone: true,
  imports: [CommonModule, RouterModule, CommonDrawerFooterComponent, TaskCreateFormComponent],
  templateUrl: './account-manager-activity-follow-up-task-drawer-wrapper.component.html',
  styleUrl: './account-manager-activity-drawer-wrapper.component.scss',
})
export class AccountManagerActivityFollowUpTaskDrawerWrapperComponent
  implements CIPXDrawerContentInterface<AccountManagerActivityFollowUpTaskDrawerData>, OnInit
{
  @Output() closeRequested = new EventEmitter<void>();

  _taskSetService = inject(MyTasksetService);
  data = inject(DRAWER_DATA, { optional: true }) as AccountManagerActivityFollowUpTaskDrawerData | null;
  drawerRef = inject(DRAWER_REF, {
    optional: true,
  }) as DrawerRef<AccountManagerActivityFollowUpTaskDrawerWrapperComponent> | null;
  selectedAccount: Account = {} as Account;
  initialTaskDraft: TaskCreateFormDraft | null = null;
  componentSelf = this;
  @ViewChild(TaskCreateFormComponent) taskCreateFormComponent?: TaskCreateFormComponent;
  drawerFooterButtons = [
    { id: 'save', label: 'Create', color: 'primary' as const, enabled: true, visible: true, action: 'submitTask' },
    { id: 'cancel', label: 'Cancel', color: '' as const, enabled: true, visible: true, action: 'closeDrawer' },
  ];

  ngOnInit(): void {
    this.drawerRef?.setTitle(this.data?.title || 'Create Follow-up Task');
    this.drawerRef?.setShowBackButton(false);
    this.drawerRef?.setShowCollapseButton(false);
    this._taskSetService.scope.set('account_tasks');
    this._taskSetService.accountId.set(this.accountId ?? 0);
    this._taskSetService.newTaskStoredState.set(null);
    this.selectedAccount = this._buildSelectedAccount();
    this.initialTaskDraft = this._hydrateTaskDraft(this.data?.taskDraft);
  }

  canClose(): boolean {
    return true;
  }

  closeDrawer(): void {
    this.closeRequested.emit();
  }

  onTaskCreateFormClose(): void {
    this._taskSetService.newTaskStoredState.set(null);
    this.closeDrawer();
  }

  submitTask(): void {
    this.taskCreateFormComponent?.submitForm();
  }

  get accountId(): number | null {
    return this.data?.accountId ?? null;
  }

  get accountLabel(): string {
    return this.data?.accountLabel || (this.accountId ? `Account ${this.accountId}` : 'Account');
  }

  get accountOverviewLink(): string | null {
    return this.accountId ? `/accounts/account-manager/${this.accountId}/overview` : null;
  }

  private _hydrateTaskDraft(draft: TaskCreateFormDraft | null | undefined): TaskCreateFormDraft | null {
    if (!draft) {
      return null;
    }
    return {
      ...draft,
      assign_to: Array.isArray(draft.assign_to) ? [...draft.assign_to] : undefined,
      team: Array.isArray(draft.team) ? [...draft.team] : undefined,
      account_user: Array.isArray(draft.account_user) ? [...draft.account_user] : undefined,
      task_related_entity: draft.task_related_entity ? { ...draft.task_related_entity } : null,
      qrAssociatedToTask: draft.qrAssociatedToTask ? { ...draft.qrAssociatedToTask } : null,
    };
  }

  private _buildSelectedAccount(): Account {
    return {
      account_id: this.accountId ?? undefined,
      account_label: this.accountLabel,
      account_users: this.data?.accountUsers ?? [],
    } as Account;
  }
}
